// src/pages/DashboardPage.js
import React, { useEffect, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck,
  Folder,
  BarChart,
  Settings,
  LifeBuoy,
  Search as SearchIcon,
  Users,
  ChevronRight,
  LogOut,
  KeyRound,
} from 'lucide-react';
import AuthContext from '../AuthContext';
import API_BASE from '../apiConfig';
import HealthChip from '../components/HealthChip';
import NewsCarousel from '../components/NewsCarousel';
import { picUrl } from '../utils/url';
import './DashboardPage.css';

/* ---------- Helpers ---------- */
function normalizeRole(raw) {
  const r = (raw || '').toString().trim().toLowerCase();
  if (['admin', 'administrator', 'adm'].includes(r)) return 'admin';
  if (['transportör', 'transportor', 'transporter', 'carrier', 'driver'].includes(r)) return 'transportör';
  return r;
}
function roleFromToken() {
  try {
    const token =
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('jwt');
    if (!token || token.split('.').length !== 3) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const fromArray = Array.isArray(payload?.roles) ? payload.roles[0] : null;
    return normalizeRole(payload?.role || payload?.userRole || fromArray);
  } catch {
    return null;
  }
}
function resolveSessionRole(currentCompany, user) {
  const direct =
    currentCompany?.role ||
    currentCompany?.user?.role ||
    currentCompany?.accountRole ||
    currentCompany?.claims?.role ||
    user?.role;

  const normDirect = normalizeRole(direct);
  if (normDirect) return normDirect;

  const fromJwt = roleFromToken();
  if (fromJwt) return fromJwt;

  return 'användare';
}

// robust getters för namn/bild oavsett backend-fält
const getName = (c) =>
  c?.name ?? c?.company_name ?? c?.companyName ?? null;
const getPic = (c) =>
  c?.profilePic ?? c?.profile_pic ?? null;

/* ---------- Component ---------- */
function DashboardPage() {
  const navigate = useNavigate();
  const { user, currentCompany, setCurrentCompany } = useContext(AuthContext);
  const [pfpError, setPfpError] = useState(false);

  // Om någon lyckats rendera Dashboard utan company (edge), styra om.
  useEffect(() => {
    if (!currentCompany) {
      navigate('/login', { replace: true });
    }
  }, [navigate, currentCompany]);

  // Självläkning: hämta färska företagsdata och uppdatera lokalt vid skillnad (bevara role)
  useEffect(() => {
    if (!currentCompany?.id) return;

    fetch(`${API_BASE}/companies`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((list) => {
        const fresh = Array.isArray(list)
          ? list.find(
              (c) => String(c.id) === String(currentCompany.id)
            )
          : null;
        if (!fresh) return;

        const beforeKey = JSON.stringify({
          name: getName(currentCompany),
          pic: getPic(currentCompany),
        });
        const afterKey = JSON.stringify({
          name: getName(fresh),
          pic: getPic(fresh),
        });

        if (beforeKey !== afterKey) {
          const preservedRole = resolveSessionRole(currentCompany, user);
          const merged = {
            ...currentCompany,
            ...fresh,
            role: preservedRole,
          };
          setCurrentCompany(merged);
          try {
            localStorage.setItem(
              'currentCompany',
              JSON.stringify(merged)
            );
          } catch {}
          setPfpError(false);
        }
      })
      .catch(() => {});
  }, [currentCompany?.id, currentCompany, setCurrentCompany, user]);

  const handleLogout = () => {
    localStorage.removeItem('currentCompany');
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('jwt');
    setCurrentCompany(null);
    navigate('/login');
  };


  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Är du säker på att du vill ta bort ditt konto? Detta går inte att ångra.'
    );
    if (!confirmed) return;

    try {
      const res = await fetch(`${API_BASE}/account/delete`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error('Delete failed');
      }

      alert('Ditt konto har raderats. Du loggas nu ut.');
      handleLogout(); // återanvänd din befintliga logout
    } catch (err) {
      console.error('Kunde inte radera konto:', err);
      alert(
        'Något gick fel när vi försökte radera ditt konto. Försök igen eller kontakta support.'
      );
    }
  };


  const handleGoToProfile = () => navigate('/profile');

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 11) return 'God morgon';
    if (hour < 17) return 'God eftermiddag';
    return 'God kväll';
  })();

  // Rollhantering (nu med både company & user)
  const resolvedRole = resolveSessionRole(currentCompany, user);
  const isAdmin = resolvedRole === 'admin';
  const isTransportor = resolvedRole === 'transportör';

  // iOS / reduced motion
  const isIOS =
    typeof navigator !== 'undefined' &&
    (/(iPhone|iPad|iPod)/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' &&
        navigator.maxTouchPoints > 1));
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const carouselInterval =
    isIOS || prefersReducedMotion ? 0 : 12000;

  const links = useMemo(() => {
    const base = [
      {
        to: '/new-case',
        label: 'Starta transport',
        Icon: Truck,
        desc: 'Skapa ny transport och fyll i uppgifter',
      },
      {
        to: '/documents',
        label: 'Sparade och skickade Transportdokument',
        Icon: Folder,
        desc: 'Sök och ladda ner tidigare dokument. Här kan du också skicka sparade Transportdokument',
      },
      {
        to: '/reports',
        label: 'Översikt och statistik',
        Icon: BarChart,
        desc: 'Rapporter',
      },
      {
        to: '/settings',
        label: 'Grunduppgifter',
        Icon: Settings,
        desc: 'Företags- och användarinställningar',
      },
      {
        to: '/support',
        label: 'Support',
        Icon: LifeBuoy,
        desc: 'Få hjälp eller kontakta oss',
      },
      {
        to: '/waste-codes',
        label: 'Sök avfallskod',
        Icon: SearchIcon,
        desc: 'Sök EWC/avfallskoder snabbt',
      },
    ];

     if (isAdmin) {
      base.push({
        to: '/user-management',
        label: 'Användarhantering',
        Icon: Users,
        desc: 'Hantera användare och roller',
      });
    }

    // Logga ut-kort för transportör (som du hade)
    if (isTransportor) {
      base.push({
        to: '/__logout',
        label: 'Logga ut',
        Icon: LogOut,
        desc: 'Logga ut från kontot',
        onClick: handleLogout,
      });
    }

    // NYTT: "Ta bort mitt konto" – för både admin & transportör
    base.push({
      to: '/__delete-account',
      label: 'Ta bort mitt konto',
      Icon: KeyRound,
      desc: 'Radera ditt användarkonto permanent',
      onClick: handleDeleteAccount,
    });

    return base;

  }, [isAdmin, isTransportor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Robust visning av namn + profilbild
  const companyName = getName(currentCompany) ?? '–';
  const rawProfilePic = getPic(currentCompany);
  const profileSrc = rawProfilePic ? picUrl(rawProfilePic) : null;

  const newsItems = [
    {
      heroTitle: `${greeting}, ${companyName}`,
      heroSubtitle:
        'Snabblänkar nedan hjälper dig komma igång direkt.',
      heroActions: null,
    },
    {
      img: '/avfallspelikanposter1.png',
      tag: 'Tips',
      title: 'Välj rätt EWC-kod – snabbguide',
      text: 'De vanligaste koderna och vad man ofta blandar ihop.',
      href: '/news/ewc-guide',
    },
    {
      img: '/news/kma_nyhet3.jpg',
      tag: 'Driftinfo',
      title: 'Planerat underhåll söndag 22:00–23:00',
      text: 'Tjänsten kan vara otillgänglig under fönstret.',
      href: '/status',
    },
  ];

  return (
    <div className="dash-bg">
      <div
        className="dash-container"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {/* Top bar */}
        <header className="dash-topbar" aria-label="Sidhuvud">
          <button
            className={`company-btn${
              isAdmin ? '' : ' company-btn--readonly'
            }`}
            onClick={isAdmin ? handleGoToProfile : undefined}
            aria-label={
              isAdmin
                ? 'Öppna profil och företagsinformation'
                : 'Företagsinformation (endast visning)'
            }
            title={isAdmin ? 'Profil' : undefined}
            type="button"
            disabled={!isAdmin}
            aria-disabled={!isAdmin}
          >
            {profileSrc && !pfpError ? (
              <img
                src={profileSrc}
                alt={`${companyName} logotyp`}
                className="company-logo"
                onError={() => setPfpError(true)}
                loading="lazy"
                decoding="async"
                fetchpriority="low"
              />
            ) : (
              <div className="company-logo-fallback" aria-hidden>
                👤
              </div>
            )}
            <div className="company-meta">
              <span
                className="company-name"
                title={companyName}
              >
                {companyName}
              </span>
              {resolvedRole && (
                <span
                  className={`role-badge role-${
                    isAdmin
                      ? 'admin'
                      : isTransportor
                      ? 'transportor'
                      : 'user'
                  }`}
                  aria-label={`Roll: ${
                    isAdmin
                      ? 'admin'
                      : isTransportor
                      ? 'transportör'
                      : 'användare'
                  }`}
                >
                  {isAdmin
                    ? 'ADMIN'
                    : isTransportor
                    ? 'TRANSPORTÖR'
                    : 'ANVÄNDARE'}
                </span>
              )}
            </div>
          </button>

          <div className="topbar-actions">
            <HealthChip />
            <button
              className="ghost-btn"
              onClick={() => navigate('/waste-codes')}
              aria-label="Snabbsök avfallskod"
              title="Sök avfallskod"
              type="button"
            >
              <SearchIcon size={18} aria-hidden />
              <span>Sök</span>
            </button>
          </div>
        </header>

        {/* Hero */}
        <section
          className="dash-hero"
          aria-label="Välkomstsektion"
        >
          <div className="dash-hero-single">
            <NewsCarousel
              items={newsItems}
              intervalMs={carouselInterval}
            />
          </div>
        </section>

        {/* Grid links */}
        <main className="dash-main" role="main">
          <nav
            className="dash-grid"
            aria-label="Snabblänkar"
            aria-live="polite"
          >
            {links.map(({ to, label, Icon, desc, onClick }) => {
              const isUserMgmt = to === '/user-management';
              const disabledCard = isUserMgmt && !isAdmin;
              const handler = onClick
                ? onClick
                : () => {
                    if (!disabledCard) navigate(to);
                  };

              return (
                <button
                  key={to}
                  type="button"
                  className="dash-card"
                  onClick={handler}
                  aria-label={label}
                  title={label}
                  disabled={disabledCard}
                  aria-disabled={disabledCard}
                >
                  <span className="card-icon" aria-hidden>
                    <Icon size={24} />
                  </span>

                  <span className="card-body">
                    <span className="card-title">{label}</span>
                    {desc && (
                      <span className="card-desc">{desc}</span>
                    )}
                  </span>

                  <span className="card-chevron" aria-hidden>
                    <ChevronRight size={20} />
                  </span>

                  <span className="focus-ring" aria-hidden />
                </button>
              );
            })}
          </nav>
        </main>

        {/* Footer */}
        <footer className="dash-footer" aria-label="Sidfot">
          <p className="footnote">
            Behöver du hjälp? Gå till{' '}
            <button
              className="linklike"
              onClick={() => navigate('/support')}
              type="button"
            >
              Support
            </button>{' '}
            eller{' '}
            <a
              href="https://www.kmacenter.se/kontakt"
              className="linklike"
              target="_blank"
              rel="noopener noreferrer"
            >
              kmacenter.se/kontakt
            </a>
            .
          </p>
        </footer>
      </div>
    </div>
  );
}

export default DashboardPage;
